import React, { useState } from "react";
import { motion } from "framer-motion";
import * as LucideIcons from "lucide-react";
import { skills } from "../data/mock";

const SkillsGrid = () => {
  const [hoveredSkill, setHoveredSkill] = useState(null);
  const [filter, setFilter] = useState("All");

  const categories = ["All", ...new Set(skills.map((skill) => skill.category))];

  const filteredSkills = filter === "All" ? skills : skills.filter((skill) => skill.category === filter);

  return (
    <section
      id="skills"
      className="grid-background"
      style={{
        padding: "100px 7.6923%",
        background: "var(--bg-primary)",
      }}
    >
      <div
        style={{
          maxWidth: "1400px",
          margin: "0 auto",
        }}
      >
        {/* Section Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          style={{
            textAlign: "center",
            marginBottom: "60px",
          }}
        >
          <h2
            style={{
              fontSize: "48px",
              fontWeight: 600,
              color: "var(--text-primary)",
              marginBottom: "16px",
            }}
          >
            Technical Arsenal
          </h2>
          <p style={{ fontSize: "18px", color: "var(--text-muted)" }}>
            Hover over skills to see real-world applications
          </p>
        </motion.div>

        {/* Category Filter */}
        <div
          style={{
            display: "flex",
            gap: "12px",
            marginBottom: "40px",
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setFilter(category)}
              style={{
                background: filter === category ? "var(--brand-primary)" : "rgba(255, 255, 255, 0.1)",
                color: filter === category ? "#000000" : "var(--text-secondary)",
                border: "none",
                borderRadius: "0px",
                padding: "10px 20px",
                fontSize: "14px",
                fontWeight: 500,
                cursor: "pointer",
                transition: "all 0.3s ease",
              }}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Skills Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
            gap: "20px",
          }}
        >
          {filteredSkills.map((skill, index) => {
            const Icon = LucideIcons[skill.icon] || LucideIcons.Box;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.02 }}
                whileHover={{ scale: 1.05, y: -4 }}
                onMouseEnter={() => setHoveredSkill(index)}
                onMouseLeave={() => setHoveredSkill(null)}
                className="glass hover-lift"
                style={{
                  padding: "24px",
                  borderRadius: "0px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "12px",
                  cursor: "pointer",
                  position: "relative",
                  transition: "all 0.3s ease",
                  border: hoveredSkill === index ? `1px solid var(--brand-primary)` : "1px solid var(--border-subtle)",
                  background: hoveredSkill === index ? "rgba(0, 255, 209, 0.03)" : "rgba(10, 10, 10, 0.7)",
                }}
              >
                <motion.div
                  animate={hoveredSkill === index ? { rotate: 360 } : { rotate: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <Icon 
                    size={36} 
                    color={hoveredSkill === index ? "var(--brand-primary)" : "var(--text-secondary)"}
                  />
                </motion.div>
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: hoveredSkill === index ? "var(--brand-primary)" : "var(--text-secondary)",
                    textAlign: "center",
                    transition: "all 0.3s ease",
                  }}
                >
                  {skill.name}
                </span>

                {/* Tooltip */}
                {hoveredSkill === index && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    style={{
                      position: "absolute",
                      bottom: "calc(100% + 10px)",
                      left: "50%",
                      transform: "translateX(-50%)",
                      background: "rgba(0, 0, 0, 0.95)",
                      border: "1px solid var(--brand-primary)",
                      padding: "12px 16px",
                      borderRadius: "0px",
                      whiteSpace: "nowrap",
                      fontSize: "12px",
                      color: "var(--text-primary)",
                      zIndex: 10,
                      pointerEvents: "none",
                    }}
                  >
                    {skill.project}
                  </motion.div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default SkillsGrid;
